import React, { useState, useRef, useEffect } from 'react';
import { COURIERS, REGIONS, couriersByRegion, buildTrackingUrl } from '../couriers';
import './HomePage.css';

const SAMPLE = '1Z999AA10123456784';

// Backend API (same logic the tracking page uses)
const API_URL = (typeof window !== 'undefined' && window.location.hostname === 'localhost')
  ? 'http://localhost:3001'
  : 'https://api.33tracking.com';

// Feature-card icons (inline SVG, currentColor)
const IconBox = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
    <path d="M3.27 6.96 12 12.01l8.73-5.05M12 22.08V12" />
  </svg>
);
const IconGlobe = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
  </svg>
);
const IconBolt = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8z" />
  </svg>
);
const IconChevron = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="tt-chev">
    <path d="m6 9 6 6 6-6" />
  </svg>
);
const IconSearch = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" />
  </svg>
);

const POLICIES = {
  support: {
    title: 'Support',
    body: [
      '33 Tracking is a free tool that helps you follow parcels from 180+ couriers in one place. Enter your tracking number, choose the courier (or leave it on Auto-detect), and we take you straight to the carrier’s official tracking page.',
      'Having trouble? Email us at support@33tracking.com and we’ll help you locate your package.',
    ],
  },
  privacy: {
    title: 'Privacy Policy',
    body: [
      '33 Tracking respects your privacy. We do not require an account and we do not sell your data.',
      'Tracking numbers you enter are used only to build the link to the relevant courier’s website and are not stored on our servers. When you continue to a courier, that carrier’s own privacy policy applies.',
      'We may use basic, anonymous analytics and cookies to understand traffic and improve the site. You can disable cookies in your browser at any time.',
    ],
  },
  terms: {
    title: 'Terms of Service',
    body: [
      '33 Tracking is an independent parcel-tracking aggregator. By using this site you agree to these terms.',
      'We provide convenient links to third-party courier tracking pages. We do not deliver parcels, and we do not control or guarantee the accuracy, availability, or timeliness of information shown on carrier websites.',
      'The service is provided “as is” without warranties of any kind. 33 Tracking is not liable for any loss arising from use of the site or reliance on third-party tracking data.',
    ],
  },
  disclaimer: {
    title: 'Disclaimer',
    body: [
      '33 Tracking is not affiliated with, endorsed by, or sponsored by any courier or postal company.',
      'All carrier names and trademarks are the property of their respective owners and are used here only to identify the destination tracking service.',
      'When you select a courier and track, you are redirected to that courier’s official website to view your shipment status.',
    ],
  },
};

// A branded courier tile (colored square with wordmark) used in the grid.
function CourierTile({ courier, active, onClick }) {
  return (
    <button className={'tt-tile' + (active ? ' is-active' : '')} onClick={onClick} title={courier.name}>
      <span className="tt-tile-badge" style={{ background: courier.color, color: courier.fg }}>
        {courier.abbr}
      </span>
      <span className="tt-tile-name">{courier.name}</span>
    </button>
  );
}

function HomePage() {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [courierId, setCourierId] = useState('17track');
  const [dropOpen, setDropOpen] = useState(false);
  const [modal, setModal] = useState(null);
  const inputRef = useRef(null);
  const dropRef = useRef(null);

  const selected = COURIERS.find((c) => c.id === courierId) || COURIERS[0];

  // Close the dropdown on outside-click / Escape
  useEffect(() => {
    if (!dropOpen) return;
    const onDown = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setDropOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [dropOpen]);

  const [resolving, setResolving] = useState(false);

  const handleTrack = async (e) => {
    e.preventDefault();
    const num = trackingNumber.trim();
    if (!num) { inputRef.current && inputRef.current.focus(); return; }

    // If the number matches OUR format (EN…JP), check if it's one of our orders.
    // If so, go to our on-site tracking page (slug with hash added automatically).
    if (/^EN\d+JP$/i.test(num)) {
      try {
        setResolving(true);
        const res = await fetch(`${API_URL}/api/resolve/${encodeURIComponent(num.toUpperCase())}`);
        if (res.ok) {
          const data = await res.json();
          if (data.found && data.slug) {
            window.location.href = `/track/${data.slug}`;
            return;
          }
        }
      } catch (err) {
        // ignore and fall through to the selected courier
      } finally {
        setResolving(false);
      }
    }

    // Otherwise (or if not ours), open the selected courier's tracking page.
    window.open(buildTrackingUrl(courierId, num), '_blank', 'noopener,noreferrer');
  };

  const chooseCourier = (id) => { setCourierId(id); setDropOpen(false); };

  // Clicking a grid tile selects that courier and focuses the input.
  const pickFromGrid = (id) => {
    setCourierId(id);
    inputRef.current && inputRef.current.focus();
    if (inputRef.current) inputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className="tt-home">
      {/* Header */}
      <header className="tt-header">
        <div className="tt-header-inner">
          <a href="/" className="tt-logo"><span className="tt-logo-mark">33</span> Tracking</a>
          <nav className="tt-nav">
            <button className="tt-nav-link" onClick={() => setModal('support')}>Support</button>
          </nav>
        </div>
      </header>

      {/* Hero + Track widget */}
      <section className="tt-hero">
        <div className="tt-hero-inner">
          <h1>Track your package, anywhere</h1>
          <p className="tt-hero-sub">
            One place to follow parcels from 180+ couriers worldwide — DHL, FedEx, UPS, USPS,
            Royal Mail, China Post, Cainiao and many more.
          </p>

          <form className="tt-track" onSubmit={handleTrack}>
            <input
              ref={inputRef}
              type="text"
              className="tt-track-input"
              placeholder="Enter your tracking number"
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              autoFocus
            />

            {/* Custom themed dropdown */}
            <div className="tt-drop" ref={dropRef}>
              <button
                type="button"
                className={'tt-drop-toggle' + (dropOpen ? ' is-open' : '')}
                onClick={() => setDropOpen((o) => !o)}
                aria-haspopup="listbox"
                aria-expanded={dropOpen}
              >
                <span className="tt-drop-current">
                  <span className="tt-drop-dot" style={{ background: selected.color }} />
                  {selected.name}
                </span>
                <IconChevron />
              </button>

              {dropOpen && (
                <div className="tt-drop-menu" role="listbox">
                  <button
                    type="button"
                    className={'tt-drop-item' + (courierId === '17track' ? ' is-sel' : '')}
                    onClick={() => chooseCourier('17track')}
                  >
                    <span className="tt-drop-dot" style={{ background: '#2563eb' }} />
                    Auto-detect (any courier)
                  </button>
                  {REGIONS.map((region) => (
                    <div key={region} className="tt-drop-group">
                      <div className="tt-drop-group-label">{region}</div>
                      {couriersByRegion(region).map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          className={'tt-drop-item' + (courierId === c.id ? ' is-sel' : '')}
                          onClick={() => chooseCourier(c.id)}
                        >
                          <span className="tt-drop-dot" style={{ background: c.color }} />
                          {c.name}
                        </button>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button type="submit" className="tt-track-btn" disabled={resolving}>
              <IconSearch /> {resolving ? 'Checking…' : 'Track'}
            </button>
          </form>

          <p className="tt-try">
            Try:{' '}
            <button type="button" className="tt-try-link" onClick={() => { setTrackingNumber(SAMPLE); setCourierId('ups'); }}>
              {SAMPLE}
            </button>
          </p>
        </div>
      </section>

      {/* Feature cards (SVG icons, no emojis) */}
      <section className="tt-features">
        <div className="tt-features-inner">
          <div className="tt-feature">
            <div className="tt-feature-icon"><IconBox /></div>
            <h3>Monitor any shipment</h3>
            <p>Track parcels from couriers and postal services across the globe — no app, no sign-up.</p>
          </div>
          <div className="tt-feature">
            <div className="tt-feature-icon"><IconGlobe /></div>
            <h3>180+ couriers</h3>
            <p>DHL, FedEx, UPS, USPS, Cainiao, China Post, YunExpress, Evri and many more in one search.</p>
          </div>
          <div className="tt-feature">
            <div className="tt-feature-icon"><IconBolt /></div>
            <h3>Straight to the source</h3>
            <p>We send you directly to the carrier&apos;s official tracking page with your number pre-filled.</p>
          </div>
        </div>
      </section>

      {/* Courier grid — grouped by region; click a tile to select it */}
      <section className="tt-couriers">
        <div className="tt-couriers-inner">
          <h2>Supported couriers</h2>
          <p className="tt-section-sub">Click a courier, enter your number, then hit Track.</p>
          {REGIONS.map((region) => (
            <div key={region} className="tt-region">
              <div className="tt-region-label">{region}</div>
              <div className="tt-courier-grid">
                {couriersByRegion(region).map((c) => (
                  <CourierTile key={c.id} courier={c} active={courierId === c.id} onClick={() => pickFromGrid(c.id)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Info / how it works */}
      <section className="tt-info">
        <div className="tt-info-inner">
          <h2>How it works</h2>
          <ol className="tt-steps">
            <li><strong>Enter your tracking number</strong> from your order confirmation or shipping email.</li>
            <li><strong>Choose the courier</strong> — or leave it on Auto-detect and we&apos;ll figure it out.</li>
            <li><strong>Hit Track</strong> and we open the carrier&apos;s official page with your shipment.</li>
          </ol>

          <h2>Why use 33 Tracking?</h2>
          <p>
            Online orders ship through dozens of different carriers, and hunting down the right
            tracking page for each one is tedious. 33 Tracking puts every major courier behind a
            single search box. Whether your parcel is moving with a global express company like DHL,
            FedEx or UPS, a national post such as USPS or Royal Mail, or a cross-border service like
            Cainiao or YunExpress, you can jump straight to live status in one step.
          </p>
          <p>
            Not sure who&apos;s delivering? Leave the courier on <strong>Auto-detect</strong> and we&apos;ll
            route your number through a universal tracker that recognises shipments from 180+ carriers
            worldwide.
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="tt-footer">
        <div className="tt-footer-inner">
          <div className="tt-footer-links">
            <button onClick={() => setModal('privacy')}>Privacy Policy</button>
            <button onClick={() => setModal('terms')}>Terms of Service</button>
            <button onClick={() => setModal('disclaimer')}>Disclaimer</button>
          </div>
          <div className="tt-footer-copy">© {new Date().getFullYear()} 33tracking.com — all rights reserved.</div>
        </div>
      </footer>

      {/* Policy / info modal */}
      {modal && POLICIES[modal] && (
        <div className="tt-modal-backdrop" onClick={() => setModal(null)}>
          <div className="tt-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="tt-modal-head">
              <h3>{POLICIES[modal].title}</h3>
              <button className="tt-modal-close" onClick={() => setModal(null)} aria-label="Close">✕</button>
            </div>
            <div className="tt-modal-body">
              {POLICIES[modal].body.map((p, i) => <p key={i}>{p}</p>)}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HomePage;
